import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';

import { cn } from '@epoch-ui/lib/utils';
import { Input } from '@epoch-ui/components/Input';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  className?: string;
  debounce?: number;
}

// TODO: Move this to @epoch-components,
// since it's not just a UI component and it includes custom logic (debounce)
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, onChange, value = '', placeholder = 'Search', debounce = 300, ...props }, ref) => {
    const [inputValue, setInputValue] = useState(value);
    const [inFocus, setInFocus] = useState(false);

    // Sync with external value
    useEffect(() => {
      setInputValue(value);
    }, [value]);

    // Effect to handle debounced changes
    useEffect(() => {
      // Don't trigger if the value matches the external value
      if (inputValue === value)
        return (): void => {
          /* noop */
        };

      let timeoutId: NodeJS.Timeout | undefined;

      if (onChange) {
        timeoutId = setTimeout(() => {
          onChange({ target: { value: inputValue } } as React.ChangeEvent<HTMLInputElement>);
        }, debounce);
      }

      return (): void => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [inputValue, onChange, debounce, value]);

    const handleClear = (): void => {
      setInputValue('');
      // For immediate clear, bypass debounce
      onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <div
        className={cn(
          'tw-relative tw-flex tw-h-[40px] tw-w-[400px] tw-cursor-pointer tw-items-center tw-rounded-[8px] tw-bg-light-grey',
          className
        )}
      >
        <FontAwesomeIcon
          icon={faSearch}
          className={cn(
            'tw-absolute tw-left-3 tw-text-gray-500 tw-transition-all',
            inFocus ? 'tw-translate-x-[-10px] tw-opacity-0' : 'tw-translate-x-0 tw-opacity-100'
          )}
        />

        <Input
          ref={ref}
          value={inputValue}
          onChange={(e): void => {
            setInputValue(e.target.value);
          }}
          onFocus={(): void => setInFocus(true)}
          onBlur={(): void => setInFocus(false)}
          placeholder={!inFocus ? placeholder : ''}
          className={cn(
            'tw-transition-padding tw-w-full tw-border-none tw-bg-transparent',
            'tw-text-sm tw-font-medium tw-text-primary-grey tw-outline-none tw-duration-200',
            inFocus ? 'tw-pl-3 tw-pr-10' : 'tw-pl-10 tw-pr-3'
          )}
          {...props}
        />

        {(inputValue || inFocus) && (
          <FontAwesomeIcon
            icon={faTimes}
            className="transition-opacity tw-absolute tw-right-3 tw-cursor-pointer tw-text-gray-500 tw-opacity-50 hover:tw-opacity-100"
            onClick={handleClear}
          />
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
